// ============================================
// FullscreenPass - Base class for fullscreen (blit) passes
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import { OffscreenTarget } from '../core/RenderPass';
import { 
  CustomPass, 
  InjectionPoint,
  type CustomPassContext 
} from './CustomPass';
import {
  type PipelineHandle,
  type ShaderHandle,
  type RenderTargetHandle,
  type TextureHandle,
  type BlendState,
  NULL_HANDLE,
  BlendMode,
  blendStateFor,
} from '../types';

// Blit shader (Slang → WGSL, compiled into ShaderCache at engine startup).
import { getCompiledShader } from './ShaderCache';

/**
 * Base class for fullscreen passes (post-processing effects, blits, etc.)
 * 
 * Provides utilities for rendering fullscreen quads with custom shaders.
 * The fullscreen triangle is generated in the vertex shader - no buffers needed.
 * 
 * @example
 * ```typescript
 * class GrayscalePass extends FullscreenPass {
 *   constructor() {
 *     super('Grayscale', InjectionPoint.BeforePostProcess);
 *   }
 *   
 *   protected getShaderWGSL(): string {
 *     return myGrayscaleWgsl;
 *   }
 * }
 * ```
 */
export abstract class FullscreenPass extends CustomPass {
  // GPU resources
  protected shader: ShaderHandle = NULL_HANDLE;
  protected pipeline: PipelineHandle = NULL_HANDLE;
  
  /** Whether to use a temporary render target for ping-pong rendering */
  protected useTempTarget = true;
  
  /** The blend mode for this pass (default: None = replace) */
  protected blendMode: BlendMode = BlendMode.None;
  
  constructor(name: string, injectionPoint: InjectionPoint = InjectionPoint.BeforePostProcess) {
    super(name, injectionPoint);
  }
  
  /**
   * Initialize shader and pipeline
   */
  protected onInitialize(ctx: IRenderContext): void {
    // Get shaders
    const vertSrc = this.getVertexShader(ctx.backend);
    const fragSrc = this.getFragmentShader(ctx.backend);
    
    // Create shader
    this.shader = ctx.createShader(vertSrc, fragSrc);
    
    // Create pipeline
    const swapFormat = ctx.getSwapChainFormat();
    this.pipeline = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [], // No vertex buffer - generated in shader
      colorTargets: [{
        format: swapFormat,
        blend: this.getBlendState(),
      }],
      primitive: {
        topology: 'triangle-list',
        cullMode: 'none',
      },
      label: `${this.name}-pipeline`,
    });
    
    // Let subclasses do additional init
    this.onCreateResources(ctx);
  }
  
  /**
   * Cleanup resources
   */
  protected onDispose(ctx: IRenderContext): void {
    this.onDestroyResources(ctx);
    
    if (this.pipeline !== NULL_HANDLE) {
      ctx.destroyPipeline(this.pipeline);
      this.pipeline = NULL_HANDLE;
    }
    if (this.shader !== NULL_HANDLE) {
      ctx.destroyShader(this.shader);
      this.shader = NULL_HANDLE;
    }
  }
  
  /**
   * Execute fullscreen pass
   */
  protected onExecute(context: CustomPassContext): void {
    const { ctx, colorTexture, width, height } = context;
    
    if (!colorTexture) return;
    
    // Set pipeline
    ctx.setPipeline(this.pipeline);
    
    // Bind source texture (WebGPU: textures are in bind group, not via uniform int)
    ctx.bindTexture(0, colorTexture);
    
    // Let subclass set its uniforms
    this.setUniforms(ctx, context);
    
    // Draw fullscreen triangle
    ctx.draw(3);
  }
  
  // ============================================
  // Override these for custom effects
  // ============================================
  
  /**
   * Get vertex shader source (WGSL). Default returns standard blit shader.
   * Override only if you need custom vertex processing.
   */
  protected getVertexShader(_backend: string): string {
    return this.getShaderWGSL();
  }
  
  /**
   * Get fragment/pixel shader source (WGSL).
   */
  protected getFragmentShader(_backend: string): string {
    return this.getShaderWGSL();
  }
  
  /**
   * Get WGSL shader source. Override for custom effects.
   */
  protected getShaderWGSL(): string {
    return getCompiledShader('blit');
  }
  
  /**
   * Override to create additional GPU resources
   */
  protected onCreateResources(ctx: IRenderContext): void {
    // Default: no-op
  }
  
  /**
   * Override to destroy additional GPU resources
   */
  protected onDestroyResources(ctx: IRenderContext): void {
    // Default: no-op
  }
  
  /**
   * Override to set custom uniforms before drawing
   */
  protected setUniforms(ctx: IRenderContext, context: CustomPassContext): void {
    // Default: just opacity
    ctx.setUniform('uOpacity', 1.0);
  }
  
  /**
   * Get blend state for the pipeline
   */
  protected getBlendState(): BlendState | undefined {
    // Shared source of truth (now also covers Screen + PremultipliedAlpha). blendStateFor
    // returns null for None; the pipeline descriptor treats `undefined` as its default
    // (standard alpha blend), so map null → undefined to preserve prior behavior.
    return blendStateFor(this.blendMode) ?? undefined;
  }
}

